import { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    const stored = localStorage.getItem("smartai_user");
    return stored ? JSON.parse(stored) : null;
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // CRITICAL: if returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    const token = localStorage.getItem("smartai_token");
    if (!token) {
      setLoading(false);
      return;
    }
    api
      .get("/auth/me")
      .then((res) => {
        setUser(res.data);
        localStorage.setItem("smartai_user", JSON.stringify(res.data));
      })
      .catch(() => {
        localStorage.removeItem("smartai_token");
        localStorage.removeItem("smartai_user");
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("smartai_token", data.access_token);
    localStorage.setItem("smartai_user", JSON.stringify(data.user));
    setUser(data.user);
    return data.user;
  };

  const register = async (name, email, password, referralCode) => {
    const lang = localStorage.getItem("smartai_lang") || "en";
    const payload = { name, email, password, lang };
    if (referralCode) payload.referral_code = referralCode;
    const { data } = await api.post("/auth/register", payload);
    localStorage.setItem("smartai_token", data.access_token);
    localStorage.setItem("smartai_user", JSON.stringify(data.user));
    setUser(data.user);
    return data.user;
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const loginWithGoogleCredential = async (credential) => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Custom Google OAuth: ID token verified server-side by /auth/google/verify
    const { data } = await api.post("/auth/google/verify", { credential });
    localStorage.setItem("smartai_token", data.session_token);
    localStorage.setItem("smartai_user", JSON.stringify(data.user));
    setUser(data.user);
    return data.user;
  };

  const loginWithAppleWeb = async ({ id_token, user: appleUser }) => {
    // Apple JS SDK returns `authorization.id_token`; backend verifies with
    // the Services ID audience and issues our own JWT.
    const { data } = await api.post("/auth/apple-web", {
      id_token,
      user: appleUser ? (typeof appleUser === "string" ? appleUser : JSON.stringify(appleUser)) : null,
    });
    localStorage.setItem("smartai_token", data.session_token);
    localStorage.setItem("smartai_user", JSON.stringify(data.user));
    setUser(data.user);
    return data.user;
  };

  const exchangeGoogleSession = async (sessionId) => {
    const { data } = await api.post("/auth/google/exchange", { session_id: sessionId });
    // Store session_token like a JWT (works around ingress CORS preventing cookie auth)
    localStorage.setItem("smartai_token", data.session_token);
    localStorage.setItem("smartai_user", JSON.stringify(data.user));
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout", {}); } catch (e) { /* ignore */ }
    localStorage.removeItem("smartai_token");
    localStorage.removeItem("smartai_user");
    setUser(null);
  };

  const refresh = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      localStorage.setItem("smartai_user", JSON.stringify(data));
      return data;
    } catch {
      return null;
    }
  };

  // Quietly re-fetch /auth/me whenever the tab regains focus AND every 60s in
  // the background. This makes admin-triggered changes (make_admin, revoke_admin,
  // plan upgrades from Stripe, bans) visible to the affected user without a
  // manual logout/login.
  useEffect(() => {
    if (!user) return undefined;
    const tick = () => { if (!document.hidden) refresh(); };
    const id = setInterval(tick, 60_000);
    window.addEventListener("focus", tick);
    return () => {
      clearInterval(id);
      window.removeEventListener("focus", tick);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const setSession = (token, userData) => {
    if (token) localStorage.setItem("smartai_token", token);
    if (userData) {
      localStorage.setItem("smartai_user", JSON.stringify(userData));
      setUser(userData);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, loginWithGoogle, loginWithGoogleCredential, loginWithAppleWeb, exchangeGoogleSession, logout, setUser, setSession, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
